import React from 'react';
import { StatusCard } from '../components/StatusCard';
import { CampaignTimeline } from '../components/CampaignTimeline';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { stats, campaigns } from '../lib/mockData';
import { Users, Mail, BookOpen, AlertCircle, ArrowRight } from 'lucide-react';
interface DashboardProps {
  onNavigate: (page: string) => void;
}
export function Dashboard({
  onNavigate
}: DashboardProps) {
  return <div className="space-y-8" data-id="element-165">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-166">
        <div data-id="element-167">
          <h1 className="text-2xl font-bold text-stone-900" data-id="element-168">
            Literacy Overview
          </h1>
          <p className="text-stone-500 mt-1" data-id="element-169">
            Track student progress and manage letter campaigns.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-170">
          <Button variant="outline" onClick={() => onNavigate('reports')} data-id="element-171">
            View Reports
          </Button>
          <Button onClick={() => onNavigate('campaigns')} data-id="element-172">New Campaign</Button>
        </div>
      </div>

      {/* Alert Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-id="element-173">
        <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} data-id="element-174" />
        <div className="flex-1" data-id="element-175">
          <h3 className="text-sm font-semibold text-amber-900" data-id="element-176">
            Fall Testing Window Closes Soon
          </h3>
          <p className="text-sm text-amber-700 mt-1" data-id="element-177">
            The testing window for "Fall 2024 Literacy Update" closes in 3 days.
            Ensure all student scores are synced before letter generation begins
            on Oct 30.
          </p>
        </div>
        <Button size="sm" variant="outline" className="bg-white border-amber-200 text-amber-800 hover:bg-amber-50" data-id="element-178">
          Review Status
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-179">
        <StatusCard title="Total Students" value={stats.totalStudents.toLocaleString()} icon={<Users size={20} data-id="element-181" />} color="stone" data-id="element-180" />
        <StatusCard title="On Track" value={`${stats.onTrack}%`} trend={{
        value: 2.4,
        label: 'vs last year',
        direction: 'up'
      }} icon={<BookOpen size={20} data-id="element-183" />} color="teal" data-id="element-182" />
        <StatusCard title="Needs Support" value={`${stats.needsSupport}%`} trend={{
        value: 1.2,
        label: 'vs last year',
        direction: 'down'
      }} icon={<AlertCircle size={20} data-id="element-185" />} color="coral" data-id="element-184" />
        <StatusCard title="Letters Sent" value="12.5k" icon={<Mail size={20} data-id="element-187" />} color="blue" data-id="element-186" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-188">
        {/* Main Content: Campaign Timeline */}
        <div className="lg:col-span-2 space-y-6" data-id="element-189">
          <div className="flex items-center justify-between" data-id="element-190">
            <h2 className="text-lg font-semibold text-stone-900" data-id="element-191">
              Campaign Schedule
            </h2>
            <button onClick={() => onNavigate('campaigns')} className="text-sm text-teal-600 font-medium hover:text-teal-700 flex items-center gap-1" data-id="element-192">
              View All <ArrowRight size={16} data-id="element-193" />
            </button>
          </div>
          <Card data-id="element-194">
            <CampaignTimeline campaigns={campaigns} data-id="element-195" />
          </Card>
        </div>

        {/* Sidebar: Quick Actions & Insights */}
        <div className="space-y-6" data-id="element-196">
          <h2 className="text-lg font-semibold text-stone-900" data-id="element-197">
            Quick Actions
          </h2>
          <Card className="space-y-2" data-id="element-198">
            <button onClick={() => onNavigate('letters')} className="w-full text-left px-4 py-3 rounded-lg hover:bg-stone-50 transition-colors flex items-center justify-between group" data-id="element-199">
              <span className="font-medium text-stone-700 group-hover:text-stone-900" data-id="element-200">
                Preview Letters
              </span>
              <ArrowRight size={16} className="text-stone-400 group-hover:text-stone-600" data-id="element-201" />
            </button>
            <div className="h-px bg-stone-100" data-id="element-202"></div>
            <button onClick={() => onNavigate('students')} className="w-full text-left px-4 py-3 rounded-lg hover:bg-stone-50 transition-colors flex items-center justify-between group" data-id="element-203">
              <span className="font-medium text-stone-700 group-hover:text-stone-900" data-id="element-204">
                Sync Scores
              </span>
              <ArrowRight size={16} className="text-stone-400 group-hover:text-stone-600" data-id="element-205" />
            </button>
            <div className="h-px bg-stone-100" data-id="element-206"></div>
            <button onClick={() => onNavigate('settings')} className="w-full text-left px-4 py-3 rounded-lg hover:bg-stone-50 transition-colors flex items-center justify-between group" data-id="element-207">
              <span className="font-medium text-stone-700 group-hover:text-stone-900" data-id="element-208">
                Configure Thresholds
              </span>
              <ArrowRight size={16} className="text-stone-400 group-hover:text-stone-600" data-id="element-209" />
            </button>
          </Card>

          <div className="bg-teal-50 rounded-xl p-5 border border-teal-100" data-id="element-210">
            <h3 className="font-semibold text-teal-900 mb-2" data-id="element-211">Did you know?</h3>
            <p className="text-sm text-teal-800 mb-4" data-id="element-212">
              Students reading on grade level by 3rd grade are 4x more likely to
              graduate high school.
            </p>
            <div className="text-xs font-medium text-teal-700 uppercase tracking-wide" data-id="element-213">
              Attend Insights
            </div>
          </div>
        </div>
      </div>
    </div>;
}